import { FashionModel } from "../../../domain/entities/product/FashionModel";
import { Product, ProductProps } from "../../../domain/entities/product/Product";
import { SizeVariant } from "../../../domain/entities/sizeVariant/SizeVariant";
import { Price } from "../../../domain/projections/Price";
import { ProductPreview } from "../../../domain/projections/ProductPreview";
import { VariantWithColorAndImage } from "../../../domain/projections/VariantWithColorAndImage";
import { I18nKey, Media } from "../../ui/shared/types";

interface BenefitsContentDTO {
  readonly imageCollageUrl: string;
}

interface ProductPreviewDTO {
  readonly id: string;
  readonly title: string;
  readonly brand: string;
  readonly price: number;
  readonly variantPrice?: Price;
  readonly imageUrl: string;
}

interface SizeVariantDTO {
  readonly variantId: string;
  readonly url: string;
  readonly isAvailable: boolean;
  readonly size: {
    readonly id: string;
    readonly name: string;
    readonly isSizeRecommended: boolean;
  };
  readonly color: { id: string; name: string };
  readonly price: {
    readonly amount: number;
    readonly currency: string;
  };
}

interface SizeVariantsDTO {
  readonly sizes: {
    readonly sizeVariants: SizeVariantDTO[];
    readonly sizeVariant: SizeVariantDTO;
  }[];
}

interface QualifierValueDTO {
  readonly key: string;
}

interface QualifierDTO {
  readonly qualifierValues: QualifierValueDTO[];
  readonly name: string;
  readonly type: string;
}

interface FeatureValueDTO {
  readonly id: string;
  readonly key: I18nKey;
  readonly qualifier?: QualifierDTO;
}

interface FeatureDTO {
  readonly id: string;
  readonly key: I18nKey;
  readonly featureValues: FeatureValueDTO[];
}

interface ProductDetailsDTO {
  readonly brand: string;
  readonly color: string;
  readonly features: FeatureDTO[];
  readonly fashionModel: FashionModel;
  readonly media?: Media[];
  readonly price: Price;
  readonly reference: string;
  readonly title: string;
  readonly wishlisted: boolean;
  readonly lastUnits: boolean;
  readonly lastUnitsStock?: boolean;
}

const mapProductDetailsDTOAndSizesDTOToProduct = (
  productDetailsDTO: ProductDetailsDTO,
  sizesDTO: SizeVariant[],
  variantId: string,
  sizeVariant: SizeVariantDTO | undefined,
): Product =>
  new Product({
    variantId,
    brand: productDetailsDTO.brand,
    color: productDetailsDTO.color,
    features: productDetailsDTO.features,
    fashionModel: productDetailsDTO.fashionModel,
    media: productDetailsDTO.media,
    price: productDetailsDTO.price,
    title: productDetailsDTO.title,
    reference: productDetailsDTO.reference,
    wishlisted: productDetailsDTO.wishlisted,
    size: sizeVariant?.size.name || "",
    sizeVariants: sizesDTO,
    lastUnits: productDetailsDTO.lastUnits,
    lastUnitsStock: productDetailsDTO.lastUnitsStock,
  } as ProductProps);

const mapCrossselingProductsDtoToProductsPreview = (crossSellingProductsDTO: ProductPreviewDTO[]): ProductPreview[] => {
  return crossSellingProductsDTO.map((product: ProductPreviewDTO) => ({
    ...product,
    price: product.variantPrice as Price,
  }));
};

const mapBenefitsContentDtoToBenefitsMediaUrl = (benefitsContentDTO: BenefitsContentDTO): string => {
  return benefitsContentDTO.imageCollageUrl;
};

const mapSizeVariantsDtoToVariantWithColorAndImages = (
  sizeVariantsDto: SizeVariantsDTO,
): VariantWithColorAndImage[] => {
  return sizeVariantsDto.sizes.flatMap((size) =>
    size.sizeVariants.map((variant) => ({
      id: variant.variantId,
      color: { id: variant.color.id, name: variant.color.name },
      url: variant.url,
      isSizeRecommended: variant.size.isSizeRecommended,
      isSizeAvailable: variant.isAvailable,
    })),
  );
};

const mapSizeVariantsDtoToSizes = (sizeVariantsDto: SizeVariantsDTO): SizeVariant[] => {
  return sizeVariantsDto.sizes.flatMap((sizeVariantDTO) => {
    const selectedColor = sizeVariantDTO.sizeVariant.color.id;

    return sizeVariantDTO?.sizeVariants.reduce((acc: SizeVariant[], sizeVariant) => {
      const colorId = sizeVariant.color.id;

      return colorId === selectedColor
        ? [
            ...acc,
            {
              available: sizeVariant.isAvailable,
              size: sizeVariant.size.name,
              variantId: sizeVariant.variantId,
              isSizeRecommended: sizeVariant.size.isSizeRecommended,
            } as SizeVariant,
          ]
        : acc;
    }, [] as SizeVariant[]);
  });
};

export type { BenefitsContentDTO, ProductDetailsDTO, SizeVariantsDTO, ProductPreviewDTO };
export {
  mapProductDetailsDTOAndSizesDTOToProduct,
  mapCrossselingProductsDtoToProductsPreview,
  mapBenefitsContentDtoToBenefitsMediaUrl,
  mapSizeVariantsDtoToVariantWithColorAndImages,
  mapSizeVariantsDtoToSizes,
};
